"use client";

import { ArrowRightIcon } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";

import QuizHeaderImage from "@/app/(discover)/candidate/(profile)/quiz/assets/quiz-header-image.png";
import QuizDescriptionCard from "@/app/(discover)/candidate/(profile)/quiz/components/quiz-description-card";
import { QuizProfileGateCard } from "@/app/(discover)/candidate/(profile)/quiz/components/quiz-profile-gate-card";
import { QuizCompleteRedirectGate } from "@/app/(discover)/candidate/(profile)/quiz/components/quiz-redirect-gate";
import { getQuizIntroCopy } from "@/app/(discover)/candidate/(profile)/quiz/lib/season-copy";
import { withAuthGate } from "@/components/shared/auth-paywall/with-auth-gate";
import { withProfileCompletionGate } from "@/components/shared/gates/profile-completion-gate";
import { Button } from "@/components/ui/button";
import { authTestIds } from "@/lib/data-test-ids/components.data-test-ids";
import { quizDescriptionDataTestIds } from "@/lib/data-test-ids/quiz.data-test-ids";
import { ProfileCompletionState } from "@/lib/profile-completion";
import routes from "@/lib/routes";
import { User } from "@/lib/types/auth";

export function QuizPageContainer({ user }: { user: User }) {
  const router = useRouter();
  const { title, matchingMessage, isSeasonNotice } = getQuizIntroCopy(
    new Date(),
  );

  const handleStartQuiz = async () => {
    router.push(routes.getProfileQuizDefinitions());
  };

  return (
    <QuizCompleteRedirectGate userId={user.id} disabled={false}>
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex flex-col items-center">
          <Image
            src={QuizHeaderImage}
            alt="Quiz Header Image"
            width={220}
            height={220}
            className="mb-6"
            data-testid={
              quizDescriptionDataTestIds.QUIZ_DESCRIPTION_HEADER_IMAGE
            }
          />
          <h1
            className="text-2xl sm:text-3xl font-bold text-foreground mb-2 text-center"
            data-testid={quizDescriptionDataTestIds.QUIZ_DESCRIPTION_TITLE}
          >
            {title}
          </h1>
          <p className="text-sm text-ink-subtle mb-8 text-center">
            Find schools that match what matters most to you
          </p>
          <QuizDescriptionCard
            matchingMessage={matchingMessage}
            isSeasonNotice={isSeasonNotice}
          />
          <Button
            onClick={handleStartQuiz}
            className="bg-surface-inverted hover:bg-surface-inverted/90 h-11 text-sm font-semibold px-8 rounded-lg mt-2"
            data-testid={
              quizDescriptionDataTestIds.QUIZ_DESCRIPTION_START_QUIZ_BUTTON
            }
          >
            Start Quiz
            <ArrowRightIcon className="size-4" />
          </Button>
        </div>
      </div>
    </QuizCompleteRedirectGate>
  );
}

export default withAuthGate(
  withProfileCompletionGate(QuizPageContainer, {
    minState: ProfileCompletionState.REQUIRES_CREDENTIALS,
    customGate: (_props, { profileCompletionState }) => (
      <QuizProfileGateCard state={profileCompletionState} />
    ),
  }),
  {
    testId: authTestIds.SIGN_IN_QUIZ_TAB,
  },
);
